import {
  FilterTransactionClientDTO,
  TransactionDTO,
  TransactionDTOImpl,
  TransactionDTOTransactionTypeEnum
} from '@/models'
import api from '@/services/api'
import { defineStore } from 'pinia'

export const useTransactionsHistoryStore = defineStore({
  id: 'transactionsHistory',
  state: () => ({
    transactions: [] as TransactionDTO[],
    loading: false,
    page: 1,
    itemPerPage: 10,
    totalItems: 0,
    search: '',
    exportLoading: false,
    openPendingDialog: false,
    exportPayload: {
      transactionType: TransactionDTOTransactionTypeEnum.MOBILETRANSFERE,
      fromDate: new Date().toISOString(),
      toDate: new Date().toISOString()
    } as FilterTransactionClientDTO
  }),
  getters: {
    getTransactions: function (state) {
      return state.transactions
    },
    displayedItems: function (state) {
      return state.transactions.slice(0, state.itemPerPage)
    }
  },
  actions: {
    getAllTransactionsByUser: async function () {
      this.transactions = []
      this.loading = true

      // Update filter
      this.exportPayload = {
        fromDate: null,
        toDate: null,
        transactionType: null
      }

      await api
        .get('/api/web/transactions', {
          params: {
            page: this.page - 1,
            size: this.itemPerPage
          }
        })
        .then(response => {
          this.transactions = TransactionDTOImpl.listFromJson(response.data)
          this.totalItems = response.headers['x-total-count']
        })
        .finally(() => {
          this.loading = false
        })
    },

    getTransactionsByUserAndType: async function (
      transactionType: TransactionDTOTransactionTypeEnum,
      dateRange: string[]
    ) {
      if (dateRange.length > 0) {
        await this.getTransactionsByUserAndDateRangeAndType(
          transactionType,
          new Date(dateRange[0]),
          new Date(dateRange[1])
        )
        return
      }
      this.transactions = []
      this.loading = true

      // Update filter
      this.exportPayload = {
        fromDate: null,
        toDate: null,
        transactionType: transactionType
      }

      await api
        .get(`/api/web/transactions/type/${transactionType}`, {
          params: {
            page: this.page - 1,
            size: this.itemPerPage
          }
        })
        .then(response => {
          this.transactions = TransactionDTOImpl.listFromJson(response.data)
          this.totalItems = response.headers['x-total-count']
        })
        .finally(() => {
          this.loading = false
        })
    },

    getTransactionsByUserAndDateRange: async function (
      startDate: Date,
      endDate: Date,
      transactionType?: TransactionDTOTransactionTypeEnum
    ) {
      if (transactionType !== undefined) {
        await this.getTransactionsByUserAndDateRangeAndType(transactionType, startDate, endDate)
        return
      }
      this.transactions = []
      this.loading = true

      // Update filter
      this.exportPayload = {
        fromDate: startDate.toISOString(),
        toDate: endDate.toISOString(),
        transactionType: null
      }

      await api
        .get(`/api/transactions/date`, {
          params: {
            dateDebut: startDate.toISOString(),
            dateFin: endDate.toISOString(),
            page: this.page - 1,
            size: this.itemPerPage
          }
        })
        .then(response => {
          this.transactions = TransactionDTOImpl.listFromJson(response.data)
          this.totalItems = response.headers['x-total-count']
        })
        .finally(() => {
          this.loading = false
        })
    },

    getTransactionsByUserAndDateRangeAndType: async function (
      transactionType: TransactionDTOTransactionTypeEnum,
      startDate: Date,
      endDate: Date
    ) {
      this.transactions = []
      this.loading = true

      // Update filter
      this.exportPayload = {
        fromDate: startDate.toISOString(),
        toDate: endDate.toISOString(),
        transactionType
      }

      await api
        .get(`/api/transactions/type/${transactionType}/date`, {
          params: {
            dateDebut: startDate.toISOString(),
            dateFin: endDate.toISOString(),
            page: this.page - 1,
            size: this.itemPerPage
          }
        })
        .then(response => {
          this.transactions = TransactionDTOImpl.listFromJson(response.data)
          this.totalItems = response.headers['x-total-count']
        })
        .finally(() => {
          this.loading = false
        })
    },

    closePendingDialog() {
      this.openPendingDialog = false
    },

    async exportToPDF() {
      try {
        this.exportLoading = true
        const response = await api.post('/api/transactions/export/pdf', JSON.stringify(this.exportPayload))
        if (response.status === 200) {
          this.openPendingDialog = true
        }
      } catch (error) {
        console.error(error)
      } finally {
        this.exportLoading = false
      }
    },

    async exportToExcel() {
      try {
        this.exportLoading = true
        const response = await api.post('/api/transactions/export/excel', JSON.stringify(this.exportPayload))
        if (response.status === 200) {
          this.openPendingDialog = true
        }
      } catch (error) {
        console.error(error)
      } finally {
        this.exportLoading = false
      }
    }
  }
})
